import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router"
import { Header } from "@/components/Header"

export const Route = createFileRoute("/(authenticated)")({
  beforeLoad: ({ context, location }) => {
    if (!context.user) {
      throw redirect({
        to: "/auth/$pathname" as const,
        params: { pathname: "sign-in" },
        search: {
          from: location.href,
        },
      })
    }
  },
  component: AppLayout,
})

export function AppLayout() {
  // Allow certain routes to use full-bleed layout (no container max-width)
  const routerState = useRouterState()
  const pathname = routerState.location.pathname

  // Full-bleed pages that manage their own scrolling completely
  const isEditorPage =
    pathname.startsWith("/projects/new") ||
    pathname.endsWith("/configure") ||
    pathname.endsWith("/crop") ||
    pathname.endsWith("/optimize") ||
    pathname.endsWith("/process") ||
    pathname.endsWith("/upload")

  // Settings uses full-bleed layout but manages its own scrolling via sidebar
  const isSettings = pathname.startsWith("/settings") || pathname.startsWith("/list-of-materials")

  // Full-height sections under the app header manage their own scrolling.
  const isBulkUpdates = pathname.startsWith("/bulk-updates")
  const isDocs = pathname.startsWith("/docs")
  if (isBulkUpdates || isDocs) {
    return (
      <div className="dark:bg-brand-darkblue-900 flex h-screen flex-col overflow-hidden bg-white">
        <Header className="shrink-0" />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    )
  }

  // Wide pages that benefit from more horizontal space (tables, lists)
  // These pages manage their own container/layout internally
  const isWidePage =
    pathname.startsWith("/files") ||
    pathname.startsWith("/projects") ||
    pathname.startsWith("/reports") ||
    pathname.startsWith("/search") ||
    pathname === "/no-access"

  const isFullBleed = isEditorPage || isSettings || isWidePage

  return (
    <div
      className={`dark:bg-brand-darkblue-900 flex flex-col bg-white ${isFullBleed ? "h-screen overflow-hidden" : "min-h-screen"}`}
    >
      <Header className="shrink-0" />
      <main
        className={`flex-1 ${isEditorPage ? "min-h-0 overflow-hidden" : isSettings ? "min-h-0" : isWidePage ? "min-h-0 overflow-auto px-4 py-4 sm:px-6 sm:py-6" : "px-4 py-4 sm:container sm:mx-auto sm:px-4 sm:py-6"}`}
      >
        <Outlet />
      </main>
    </div>
  )
}
